var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Drives the exterior damages step. For insurers with the
 *   GardenFurnitureAsPersonalProperty feature flag (MAPA on TNG), it also hosts a
 *   "Mobilier de jardin" sub-section that opens PersonalPropertyModal V2 and
 *   persists the entry as a PersonalProperty (category 'meuble-de-jardin') so it
 *   flows through MapaPersonalPropertyValuationComputer instead of the lighter
 *   ExternalDamage path.
 * touches: declaration flow, exterior damages, personal property valuation
 * invariants: garden furniture entries must use category slug 'meuble-de-jardin'.
 *   When the flag is on, the ExternalPersonalProperty localization is hidden from
 *   the legacy modal so users have a single path for garden furniture.
 * inputs: insurer feature flag + claim trigram, personal property category data
 * outputs: ExternalDamage entries (legacy) and PersonalProperty entries (garden
 *   furniture); only the latter is valued by the MAPA computer.
 * see: backend/src/Computer/Customer/Mapa/MapaPersonalPropertyValuationComputer.php,
 *   backend/config/customer/mapa/services.yaml,
 *   frontend/src/declaration/store/personalProperty.ts (seedGardenFurnitureChange),
 *   docs/notes/garden-furniture-as-personal-property.md
 */
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton } from '@claims-ia/design-system';
import { QuestionComponent, QuestionProps } from '@declaration/question';
import { ClaimTypeTrigram, } from '@shared/types/api/claim';
import ExternalDamageModal from '@declaration/components/externalDamage/ExternalDamageModal.vue';
import PersonalPropertyModalV2 from '@declaration/components/personalProperty/v2/PersonalPropertyModal.vue';
import DamageCard from '@declaration/components/UI/DamageCard.vue';
import QuestionPanel from '@declaration/components/UI/QuestionPanel.vue';
import ExternalDamagesList from '@declaration/components/externalDamage/ExternalDamagesList.vue';
import { EXTERNAL_DAMAGE_SLUG_TRAD_KEYS } from '@shared/constants/externalDamages';
import { InsuranceContractXmlLabel } from '@shared/types/api/contract';
import { ExclusionReason } from '@shared/types/api/refundDetail';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
import { optionalResourceIri } from '@shared/utils/iri';
import { PERSONAL_PROPERTY_QUESTION_TRAD_KEYS, } from '@declaration/types/personalProperty';
import { personalPropertyFields } from '@shared/personalProperty/personalPropertyFields';
import { ModelFieldsProjection } from '@shared/modelField/modelField';
import { Step } from '@shared/claim/steps';
const externalDamage = namespace('externalDamage');
const personalProperty = namespace('personalProperty');
const contract = namespace('contract');
const qualification = namespace('qualification');
const settings = namespace('settings');
let ExternalDamagesQuestion = class ExternalDamagesQuestion extends QuestionComponent {
    constructor() {
        super(...arguments);
        this.isSaving = false;
        this.externalDamage = null;
        this.selectedChangeKey = null;
        this.isExternalDamageModalOpen = false;
        this.isPersonalPropertyModalOpen = false;
        this.selectedPersonalPropertyChangeKey = null;
        this.pendingDeletePersonalProperty = null;
    }
    get selfCareSettings() {
        return this.setting('selfCareSettings');
    }
    get hasGardenFurnitureAsPersonalProperty() {
        return insurerHasFeature(InsurerFeatureFlag.GardenFurnitureAsPersonalProperty, this.typeTrigram);
    }
    get gardenFurnitureSaveStep() {
        return Step.ExternalDamagesDetails;
    }
    get gardenFurnitureDamages() {
        if (!this.hasGardenFurnitureAsPersonalProperty || !this.gardenFurnitureCategoryIri) {
            return [];
        }
        return this.personalPropertyChangedDamages.filter((pp) => optionalResourceIri(pp.category) === this.gardenFurnitureCategoryIri);
    }
    get selectedGardenFurnitureIndex() {
        return this.gardenFurnitureDamages.length;
    }
    get gardenFurnitureTradKeys() {
        return PERSONAL_PROPERTY_QUESTION_TRAD_KEYS[this.typeTrigram];
    }
    gardenFurnitureTitle(damage, index) {
        return (damage.designation ||
            `${this.$tc('claim.declaration.external_damages.garden_furniture.section_title')} ${index + 1}`);
    }
    gardenFurnitureModelFields(damage) {
        return personalPropertyFields(damage, {
            edition: false,
            type: this.typeTrigram,
            projection: ModelFieldsProjection.Declaration,
            translate: this.$tc.bind(this),
            categories: this.personalPropertyCategoriesByIri,
        });
    }
    addNewDamage() {
        this.selectedChangeKey = 0;
        this.isExternalDamageModalOpen = true;
    }
    deleteDamageHandler(externalDamage) {
        this.externalDamage = externalDamage;
        this.confirmDelete(externalDamage.id, this.deleteExternalDamage);
    }
    confirmDelete(externalDamageIri, deleteCallback) {
        if (externalDamageIri) {
            this.$buefy.dialog.confirm({
                animation: 'fade',
                message: this.$tc('claim.damage.external_damages.form.delete.modal.text'),
                cancelText: this.$tc('claim.damage.external_damages.form.delete.modal.cancel'),
                confirmText: this.$tc('claim.damage.external_damages.form.delete.modal.confirm'),
                onConfirm: deleteCallback,
            });
        }
        else {
            deleteCallback();
        }
    }
    selectExternalDamageAndOpenModal(externalDamage) {
        this.selectedChangeKey = externalDamage.id;
        this.isExternalDamageModalOpen = true;
    }
    closeModal() {
        this.isExternalDamageModalOpen = false;
    }
    openGardenFurnitureModal(damage) {
        if (damage && damage.id) {
            this.selectedPersonalPropertyChangeKey = damage.id;
        }
        else {
            this.selectedPersonalPropertyChangeKey = 0;
            this.seedGardenFurnitureChange(0);
        }
        this.isPersonalPropertyModalOpen = true;
    }
    onGardenFurnitureSelectedFromExternalModal() {
        this.isExternalDamageModalOpen = false;
        this.resetExternalDamageChanges();
        this.openGardenFurnitureModal(null);
    }
    closePersonalPropertyModal() {
        this.isPersonalPropertyModalOpen = false;
        this.resetPersonalPropertyChanges();
    }
    onDeleteGardenFurniture(damage) {
        this.pendingDeletePersonalProperty = damage;
        this.confirmDeletePersonalProperty(damage.id, this.deleteGardenFurniture);
    }
    confirmDeletePersonalProperty(personalPropertyIri, deleteCallback) {
        if (personalPropertyIri) {
            this.$buefy.dialog.confirm({
                animation: 'fade',
                message: this.$tc('claim.declaration.external_damages.garden_furniture.delete.modal.text'),
                cancelText: this.$tc('claim.declaration.external_damages.garden_furniture.delete.modal.cancel'),
                confirmText: this.$tc('claim.declaration.external_damages.garden_furniture.delete.modal.confirm'),
                onConfirm: deleteCallback,
            });
        }
        else {
            deleteCallback();
        }
    }
    async deleteGardenFurniture() {
        this.isSaving = true;
        try {
            const damage = this.pendingDeletePersonalProperty;
            if (damage === null || damage === void 0 ? void 0 : damage.id) {
                await this.deletePersonalPropertyDamage(damage.id);
                await this.savePersonalPropertyChanges({ step: this.gardenFurnitureSaveStep });
            }
        }
        finally {
            this.pendingDeletePersonalProperty = null;
            this.isSaving = false;
        }
    }
    async deleteExternalDamage() {
        var _a, _b;
        this.isSaving = true;
        if ((_a = this.externalDamage) === null || _a === void 0 ? void 0 : _a.id) {
            await this.deleteDamage((_b = this.externalDamage) === null || _b === void 0 ? void 0 : _b.id);
            await this.saveDamage();
        }
        else {
            await this.deleteDamage(this.selectedChangeKey);
        }
        this.isSaving = false;
    }
    get areOutbuildingsExcluded() {
        if (this.applicableContractDetail === null) {
            return false;
        }
        for (const contractCategoriesCondition of this.applicableContractDetail
            .contractCategoriesConditions) {
            if (contractCategoriesCondition.insuranceContractXmlLabel ===
                InsuranceContractXmlLabel.Outbuilding) {
                return contractCategoriesCondition.isExcluded;
            }
        }
        return false;
    }
    get panelTitle() {
        return this.$tc('claim.declaration.external_damages.question.title');
    }
    get subTitle() {
        var _a, _b, _c, _d;
        return ((_d = (_c = (_b = (_a = this.selfCareSettings) === null || _a === void 0 ? void 0 : _a.declaration) === null || _b === void 0 ? void 0 : _b.pages) === null || _c === void 0 ? void 0 : _c.externalDamagesQuestion) === null || _d === void 0 ? void 0 : _d.showSubTitle)
            ? this.$tc('claim.declaration.external_damages.question.description')
            : null;
    }
    get sidebarTitle() {
        const key = 'claim.declaration.external_damages.question.sidebar_title';
        return this.$te(key) ? this.$tc(key) : this.panelTitle;
    }
    get answer() {
        if (!this.externalDamagesList.length)
            return this.$tc('claim.damage.external_damages.question.answer.no_damages');
        const qualification = this.qualification;
        const externalDamagesDetail = this.externalDamagesList
            .filter((damage) => !!damage.slug)
            .map((damage) => {
            const key = 'causeType' in qualification ? qualification.causeType : ClaimTypeTrigram.Fire;
            if (!key)
                return '';
            const tradKey = EXTERNAL_DAMAGE_SLUG_TRAD_KEYS[key][damage.slug];
            return this.$tc(typeof tradKey === 'string' ? tradKey : tradKey.sumupTradKey).concat(damage.refund.parameters.exclusionReason === ExclusionReason.ContractGuarantees
                ? ` (${this.$tc('claim.declaration.external_damages.question.info.not_covered')})`
                : '');
        })
            .join(', ');
        return this.$tc('claim.damage.external_damages.question.answer.with_damages', 0, {
            detail: externalDamagesDetail,
        });
    }
    get areExternalDamagesComplete() {
        const externalDamagesOk = this.areDamagesValid &&
            this.externalDamagesList.every((externalDamage) => {
                return externalDamage.id;
            });
        if (!externalDamagesOk)
            return false;
        if (!this.hasGardenFurnitureAsPersonalProperty)
            return true;
        return this.gardenFurnitureDamages.every((damage) => damage.id);
    }
    get validationButtonText() {
        const hasAnyDamage = this.externalDamagesList.length > 0 || this.gardenFurnitureDamages.length > 0;
        return hasAnyDamage
            ? this.$tc('claim.declaration.external_damages.validate.with_damages')
            : this.$tc('claim.declaration.external_damages.validate.no_damages');
    }
    askToComplete() {
        this.$buefy.toast.open({
            message: this.$tc('claim.declaration.external_damages.validate.error.ask_to_complete'),
            type: 'is-danger',
            position: 'is-bottom',
        });
    }
    async handleValidationClick() {
        this.isSaving = true;
        await this.validateExternalDamagesStep();
        this.isSaving = false;
    }
};
__decorate([
    qualification.State('qualification')
], ExternalDamagesQuestion.prototype, "qualification", void 0);
__decorate([
    qualification.State('typeTrigram')
], ExternalDamagesQuestion.prototype, "typeTrigram", void 0);
__decorate([
    externalDamage.State('damages')
], ExternalDamagesQuestion.prototype, "externalDamagesList", void 0);
__decorate([
    externalDamage.Getter('areDamagesValid')
], ExternalDamagesQuestion.prototype, "areDamagesValid", void 0);
__decorate([
    externalDamage.Action('validateExternalDamagesStep')
], ExternalDamagesQuestion.prototype, "validateExternalDamagesStep", void 0);
__decorate([
    externalDamage.Action('deleteDamage')
], ExternalDamagesQuestion.prototype, "deleteDamage", void 0);
__decorate([
    externalDamage.Action('saveChanges')
], ExternalDamagesQuestion.prototype, "saveDamage", void 0);
__decorate([
    externalDamage.Action('resetChanges')
], ExternalDamagesQuestion.prototype, "resetExternalDamageChanges", void 0);
__decorate([
    personalProperty.Getter('gardenFurnitureCategoryIri')
], ExternalDamagesQuestion.prototype, "gardenFurnitureCategoryIri", void 0);
__decorate([
    personalProperty.Getter('changedDamages')
], ExternalDamagesQuestion.prototype, "personalPropertyChangedDamages", void 0);
__decorate([
    personalProperty.State('categoriesByIri')
], ExternalDamagesQuestion.prototype, "personalPropertyCategoriesByIri", void 0);
__decorate([
    personalProperty.Action('seedGardenFurnitureChange')
], ExternalDamagesQuestion.prototype, "seedGardenFurnitureChange", void 0);
__decorate([
    personalProperty.Action('deleteDamage')
], ExternalDamagesQuestion.prototype, "deletePersonalPropertyDamage", void 0);
__decorate([
    personalProperty.Action('saveChanges')
], ExternalDamagesQuestion.prototype, "savePersonalPropertyChanges", void 0);
__decorate([
    personalProperty.Action('resetChanges')
], ExternalDamagesQuestion.prototype, "resetPersonalPropertyChanges", void 0);
__decorate([
    contract.State('applicableContractDetail')
], ExternalDamagesQuestion.prototype, "applicableContractDetail", void 0);
__decorate([
    settings.Getter('setting')
], ExternalDamagesQuestion.prototype, "setting", void 0);
ExternalDamagesQuestion = __decorate([
    Component({
        components: {
            QuestionPanel,
            ExternalDamagesList,
            UiButton,
            ExternalDamageModal,
            PersonalPropertyModalV2,
            DamageCard,
        },
        props: QuestionProps,
    })
], ExternalDamagesQuestion);
export default ExternalDamagesQuestion;
